import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Modal, Button, notification, Input, Form, Row, Col } from 'antd';
import useModal from 'stores/useModal';
import { useMutation } from '@tanstack/react-query';
import API from 'services/api';
import { useGetBookingConfirmationTemplate } from 'hooks/useGetBookingConfirmationTemplate';
import 'styles/modal-booking-confirmation.scss';
import { BookingConfirmationTemplate } from 'services/api/type/booking.type';

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { EmailRegex } from 'constants/regex';
import { getMessageCode } from 'constants/validate';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'utils';

const BookingConfirmation: React.FC = () => {
  const [form] = Form.useForm();
  const htmlRef = useRef<string>('');
  const { t } = useTranslation();
  const MESSAGE_CODE = getMessageCode(t);
  const {
    isOpenBookingConfirmation,
    setIsOpenBookingConfirmation,
    setInfoConfirmModal,
    setConfirmLoading,
    bookingId
  } = useModal();

  const [localTemplate, setLocalTemplate] = useState<BookingConfirmationTemplate[]>([]);

  const { data: bookingConfirmationTemplate } = useGetBookingConfirmationTemplate(
    Number(bookingId)
  );

  const convertStaticBoxesToCheckboxes = () => {
    const modalContent = document.getElementById('modal-content');
    if (!modalContent) return;

    const spans = modalContent.querySelectorAll('span');

    spans.forEach(span => {
      const text = span.textContent?.trim();
      if (text === '☒' || text === '☐') {
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.checked = text === '☒';
        checkbox.style.pointerEvents = 'auto'; // ensure editable
        checkbox.style.transform = 'scale(1.2)';
        checkbox.style.cursor = 'pointer';

        // Replace span with checkbox
        span.replaceWith(checkbox);
      }
    });
  };

  const convertCheckboxesToStaticBoxes = (container: HTMLElement) => {
    const checkboxes = container.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach((el: Element) => {
      const checkbox = el as HTMLInputElement;
      const span = document.createElement('span');
      span.textContent = checkbox.checked ? '☒' : '☐';
      span.style.fontSize = '16px';
      span.style.margin = '0 2px';
      checkbox.replaceWith(span);
    });
  };

  useEffect(() => {
    if (bookingConfirmationTemplate?.length && isOpenBookingConfirmation) {
      htmlRef.current = bookingConfirmationTemplate[0].body_html || '';
      console.log('Booking Confirmation Template:', bookingConfirmationTemplate[0].body_html);
      setLocalTemplate(bookingConfirmationTemplate);
      form.setFieldsValue({
        email_to: bookingConfirmationTemplate[0].email_to,
        subject: bookingConfirmationTemplate[0].subject
      });
      setTimeout(() => {
        cleanUpServiceTableLayout();
        convertStaticBoxesToCheckboxes();
      }, 100);
    }
  }, [bookingConfirmationTemplate, isOpenBookingConfirmation, form]);

  useEffect(() => {
    if (!isOpenBookingConfirmation) {
      form.resetFields();
    }
    // eslint-disable-next-line
  }, [isOpenBookingConfirmation]);

  const { mutateAsync: mutateSendBookingConfirmation } = useMutation({
    mutationFn: (body: BookingConfirmationTemplate) => API.booking.sendBookingConfirmation(body)
  });

  const handleCancel = () => {
    setIsOpenBookingConfirmation(false, undefined);
  };

  const handleContentChange = useCallback(() => {
    const modalContent = document.getElementById('modal-content');
    if (modalContent) {
      const newHtmlContent = modalContent.innerHTML;

      if (localTemplate.length > 0) {
        const updatedTemplate = [...localTemplate];
        updatedTemplate[0] = {
          ...updatedTemplate[0],
          body_html: newHtmlContent
        };
        setLocalTemplate(updatedTemplate);
      }
    }
  }, [localTemplate]);

  const enableServiceRowInsertByEnter = useCallback(() => {
    const modalContent = document.getElementById('modal-content');
    if (!modalContent) return;

    const getScrollableAncestor = (el: HTMLElement): HTMLElement | null => {
      while (el.parentElement) {
        el = el.parentElement;
        const style = getComputedStyle(el);
        const overflowY = style.overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
          return el;
        }
      }
      return null;
    };

    const handleEnterKey = (e: KeyboardEvent, currentRow: HTMLTableRowElement) => {
      e.preventDefault();

      const newRow = document.createElement('tr');
      newRow.innerHTML = `
      <td contenteditable="true" colspan="2" style="border:1px solid #ccc;padding:1px;font-family:'Cambria';color:#000;font-size:16px;text-align:center;font-weight:400;">New Service</td>
      <td contenteditable="true" style="border:1px solid #ccc;padding:1px;font-family:'Montserrat', 'Cambria';color:#000;font-size:16px;text-align:center;font-weight:400;">1</td>
      <td contenteditable="true" style="border:1px solid #ccc;padding:1px;font-family:'Montserrat', 'Cambria';color:#000;font-size:16px;text-align:center;font-weight:400;">0</td>
      <td contenteditable="true" style="border:1px solid #ccc;padding:1px;font-family:'Montserrat', 'Cambria';color:#000;font-size:16px;text-align:center;font-weight:400;">${formatCurrency(0)}</td>
    `;

      currentRow.parentNode?.insertBefore(newRow, currentRow.nextSibling);

      setTimeout(() => {
        const editableCell = newRow.querySelector('td[contenteditable="true"]') as HTMLElement;
        if (!editableCell) return;

        const range = document.createRange();
        range.selectNodeContents(editableCell);
        range.collapse(false);

        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);

        const scrollable = getScrollableAncestor(modalContent);
        if (scrollable) {
          scrollable.scrollTop = newRow.offsetTop - scrollable.offsetTop;
        }
      }, 100);

      const cloned = modalContent.cloneNode(true) as HTMLElement;
      cloned.querySelectorAll('input[type="checkbox"]').forEach(cb => {
        const input = cb as HTMLInputElement;
        input.checked ? input.setAttribute('checked', 'true') : input.removeAttribute('checked');
      });

      htmlRef.current = cloned.innerHTML;

      const clonedForStorage = modalContent.cloneNode(true) as HTMLElement;
      convertCheckboxesToStaticBoxes(clonedForStorage);

      const updated = [...localTemplate];
      updated[0] = { ...updated[0], body_html: clonedForStorage.innerHTML };
      setLocalTemplate(updated);
    };

    const handleBackspaceKey = (
      e: KeyboardEvent,
      currentRow: HTMLTableRowElement,
      allRows: HTMLTableRowElement[]
    ) => {
      const cells = Array.from(currentRow.querySelectorAll('td'));
      const isEmpty = cells.every(td => td.textContent?.trim() === '');

      if (isEmpty && allRows.length > 1) {
        e.preventDefault();

        const previousRow = currentRow.previousElementSibling as HTMLTableRowElement;
        currentRow.remove();

        if (previousRow) {
          const firstEditable = previousRow.querySelector(
            'td[contenteditable="true"]'
          ) as HTMLElement;
          if (firstEditable) {
            const range = document.createRange();
            range.selectNodeContents(firstEditable);
            range.collapse(false);
            const sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);
            firstEditable.focus();
          }
        }

        const cloned = modalContent.cloneNode(true) as HTMLElement;
        cloned.querySelectorAll('input[type="checkbox"]').forEach(cb => {
          const input = cb as HTMLInputElement;
          input.checked ? input.setAttribute('checked', 'true') : input.removeAttribute('checked');
        });

        htmlRef.current = cloned.innerHTML;

        const updated = [...localTemplate];
        updated[0] = { ...updated[0], body_html: htmlRef.current };
        setLocalTemplate(updated);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);

      const currentRow = range.startContainer.parentElement?.closest('tr') as HTMLTableRowElement;
      const serviceTable = currentRow?.closest('table');
      if (!currentRow || !serviceTable || !serviceTable.innerText.includes('SERVICE')) return;

      const allRows = Array.from(serviceTable.querySelectorAll('tr')) as HTMLTableRowElement[];

      if (e.key === 'Enter') handleEnterKey(e, currentRow);
      if (e.key === 'Backspace') handleBackspaceKey(e, currentRow, allRows);
    };

    modalContent.addEventListener('keydown', handleKeyDown, true);
    handleKeyDown;

    return () => {
      modalContent.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [localTemplate]);

  useEffect(() => {
    if (!isOpenBookingConfirmation) return;

    const cleanup = enableServiceRowInsertByEnter();

    return () => {
      cleanup?.(); // ✅ just unbind the keydown listener, do not replace DOM
    };
  }, [isOpenBookingConfirmation, enableServiceRowInsertByEnter]);

  useEffect(() => {
    const modalContent = document.getElementById('modal-content');

    if (modalContent) {
      modalContent.addEventListener('blur', handleContentChange, true);
    }
    return () => {
      const modalContent = document.getElementById('modal-content');
      if (modalContent) {
        modalContent.removeEventListener('blur', handleContentChange, true);
      }
    };
    // eslint-disable-next-line
  }, [localTemplate]);

  const handleConvertToPdf = () => {
    const modalContent = document.getElementById('modal-content');
    if (!modalContent) return;

    const tempDiv = modalContent.cloneNode(true) as HTMLElement;
    convertCheckboxesToStaticBoxes(tempDiv);

    Object.assign(tempDiv.style, {
      position: 'absolute',
      left: '-9999px',
      top: '0',
      width: 'fit-content',
      maxWidth: 'none',
      overflow: 'visible',
      height: 'auto'
    });
    document.body.appendChild(tempDiv);

    // 🔴 Remove any buttons or artifacts
    tempDiv.querySelectorAll('.insert-row-button, .insert-row-wrapper').forEach(el => el.remove());

    html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      backgroundColor: null
    })
      .then(canvas => {
        const imgData = canvas.toDataURL('image/png');
        const pdfWidth = 210;
        const imgWidth = pdfWidth;
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

        const pdf = new jsPDF('p', 'mm', [pdfWidth, pdfHeight]);
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, pdfHeight);
        pdf.save('booking_confirmation.pdf');

        document.body.removeChild(tempDiv);
      })
      .catch(err => {
        console.error('Error generating PDF:', err);
        document.body.removeChild(tempDiv);
      });
  };

  const confirmSendEmail = () => {
    setInfoConfirmModal(true, {
      title: t('common.confirm.sendEmail'),
      okText: t('common.actions.send'),
      onOk: async () => {
        const modalContent = document.getElementById('modal-content');
        if (!modalContent || localTemplate.length === 0) return;

        const tempDiv = modalContent.cloneNode(true) as HTMLElement;
        convertCheckboxesToStaticBoxes(tempDiv);

        Object.assign(tempDiv.style, {
          position: 'absolute',
          left: '-9999px',
          top: '0',
          width: 'fit-content',
          maxWidth: 'none',
          overflow: 'visible',
          height: 'auto'
        });
        document.body.appendChild(tempDiv);

        // 🔴 Remove unwanted elements
        tempDiv
          .querySelectorAll('.insert-row-button, .insert-row-wrapper')
          .forEach(el => el.remove());

        const cleanedHtml = tempDiv.innerHTML;
        document.body.removeChild(tempDiv);

        htmlRef.current = cleanedHtml;

        const updatedTemplate = [...localTemplate];
        updatedTemplate[0] = {
          ...updatedTemplate[0],
          body_html: cleanedHtml
        };
        setLocalTemplate(updatedTemplate);

        try {
          setConfirmLoading(true);
          await mutateSendBookingConfirmation(updatedTemplate[0]);
          setInfoConfirmModal(false);
          htmlRef.current = updatedTemplate[0].body_html || '';
          setLocalTemplate(updatedTemplate);
          setTimeout(() => {
            cleanUpServiceTableLayout();
            convertStaticBoxesToCheckboxes(); // ← this line does the trick
          }, 100);

          notification.success({
            message: t('common.message.sendEmailSuccessfully')
          });
        } catch (err: any) {
          notification.error({
            message: t('common.message.sendEmailFailed')
          });
        } finally {
          setConfirmLoading(false);
        }
      }
    });
  };

  const cleanUpServiceTableLayout = () => {
    const modalContent = document.getElementById('modal-content');
    if (modalContent) {
      const serviceTable = Array.from(modalContent.querySelectorAll('table')).find(tbl =>
        (tbl as HTMLElement).innerText.includes('SERVICE')
      );

      if (serviceTable) {
        const rows = serviceTable.querySelectorAll('tr');
        rows.forEach((row: HTMLTableRowElement) => {
          // Clean up row height
          row.style.height = 'auto';
          row.removeAttribute('height');
          const currentStyle = row.getAttribute('style');
          if (currentStyle?.includes('height:0pt')) {
            row.setAttribute('style', currentStyle.replace(/height:\s*0pt;?/gi, ''));
          }

          // Clean <td> styles
          row.querySelectorAll('td').forEach((td: Element) => {
            (td as HTMLTableCellElement).style.verticalAlign = 'middle';
            (td as HTMLTableCellElement).style.paddingTop = '6px';
            (td as HTMLTableCellElement).style.paddingBottom = '6px';
          });

          // Clean <p> height
          row.querySelectorAll('p').forEach((p: HTMLElement) => {
            const pStyle = p.getAttribute('style');
            if (pStyle?.includes('height')) {
              p.setAttribute('style', pStyle.replace(/height:\s*\d+(\.\d+)?(pt|px);?/gi, ''));
            }
            p.style.height = 'auto';
          });
        });
      }
    }
  };

  const handleRefreshContents = () => {
    if (bookingConfirmationTemplate?.length) {
      const newHtml = bookingConfirmationTemplate[0]?.body_html || '';
      htmlRef.current = newHtml;
      const modalContent = document.getElementById('modal-content');
      if (modalContent) {
        modalContent.innerHTML = newHtml; // ← force refresh manually
      }

      setLocalTemplate(bookingConfirmationTemplate);

      form.setFieldsValue({
        email_to: bookingConfirmationTemplate[0]?.email_to,
        subject: bookingConfirmationTemplate[0]?.subject
      });

      setTimeout(() => {
        cleanUpServiceTableLayout();
        convertStaticBoxesToCheckboxes(); // ✅ re-enable checkbox interaction
      }, 100);

      notification.success({
        message: t('common.message.refreshedSuccessfully')
      });
    } else {
      notification.error({
        message: t('common.message.notFound')
      });
    }
  };

  return (
    <Modal
      title={t('bookingConfirmation.title')}
      className="pms-modal-booking-confirmation"
      centered
      width={1200}
      open={isOpenBookingConfirmation}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
    >
      <Form form={form}>
        <div className="pms-modal-booking-confirmation__container">
          <div
            id="modal-content"
            contentEditable="true"
            className="pms-modal-booking-confirmation__content"
            dangerouslySetInnerHTML={{
              __html: htmlRef.current
            }}
          />
          <div className="pms-modal-booking-confirmation__button_and_template_info">
            <div className="pms-modal-booking-confirmation__template_info">
              <Row gutter={[8, 8]}>
                <Col span={5} className="label">
                  Email:{' '}
                </Col>
                <Col span={19}>
                  <Form.Item
                    name="email_to"
                    rules={[
                      {
                        pattern: EmailRegex,
                        message: MESSAGE_CODE.WRONG_FORMAT_EMAIL
                      }
                    ]}
                  >
                    <Input
                      onChange={e => {
                        const newEmail = e.target.value;
                        if (localTemplate.length > 0) {
                          const updatedTemplate = [...localTemplate];
                          updatedTemplate[0] = {
                            ...updatedTemplate[0],
                            email_to: newEmail
                          };
                          setLocalTemplate(updatedTemplate);
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={5} className="label">
                  {t('bookingConfirmation.subject')}:{' '}
                </Col>
                <Col span={19}>
                  <Form.Item name="subject">
                    <Input
                      onChange={e => {
                        const newSubject = e.target.value;
                        if (localTemplate.length > 0) {
                          const updatedTemplate = [...localTemplate];
                          updatedTemplate[0] = {
                            ...updatedTemplate[0],
                            subject: newSubject
                          };
                          setLocalTemplate(updatedTemplate);
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <Button type="primary" className="ant-btn-secondary" onClick={confirmSendEmail}>
              {t('common.actions.send')}
            </Button>
            <Button
              type="primary"
              className="ant-btn-secondary"
              onClick={() => handleRefreshContents()}
            >
              {t('bookingConfirmation.reset')}
            </Button>

            <Button type="primary" className="ant-btn-secondary" onClick={handleConvertToPdf}>
              {t('bookingConfirmation.saveToPdf')}
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default BookingConfirmation;
